import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import { UNIT_HOURS } from './summaryUnits';
import TimeSummaryFilters, { PERIOD_CUSTOM, PERIOD_LAST_6_WEEKS } from './TimeSummaryFilters';
import TimeSummaryTable from './TimeSummaryTable';
import TimeSummaryTiles from './TimeSummaryTiles';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';

const EMPTY_LIST = [];

/**
 * The filters as query parameters. The download and the data endpoint read the
 * same object, so the sheet always matches the table it was downloaded from.
 */
function queryFor(filters) {
    const params = { period: filters.period };

    if (filters.period === PERIOD_CUSTOM) {
        params.from = filters.from;
        params.to = filters.to;
    }

    if (filters.project) {
        params.project = filters.project;
    }

    return params;
}

// A custom range is only asked for once both ends are filled and in order —
// anything else is still being typed.
const isComplete = (filters) => filters.period !== PERIOD_CUSTOM || (!!filters.from && !!filters.to && filters.from <= filters.to);

export default function MyTimeSummaryIndex({ dataUrl, exportUrl, recordUrl }) {
    const [filters, setFilters] = useState({ period: PERIOD_LAST_6_WEEKS, from: '', to: '', project: '' });
    // Null until the first load says what the organisation records in.
    const [unit, setUnit] = useState(null);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const readSeq = useRef(0);

    const fetchReport = useCallback(() => {
        if (!isComplete(filters)) {
            return;
        }

        const sequence = ++readSeq.current;

        setLoading(true);
        setError(null);

        axios
            .get(dataUrl, { params: queryFor(filters) })
            .then(({ data }) => {
                // Only the newest read may land: the filters stay live while one
                // is in flight.
                if (sequence === readSeq.current) {
                    setReport(data.data);
                    setUnit((current) => current ?? data.data.options.unit);
                }
            })
            .catch((e) => {
                if (sequence === readSeq.current) {
                    // A refused custom range carries a sentence worth showing.
                    const errors = e?.response?.data?.errors;
                    setError((errors && Object.values(errors)[0]?.[0]) || $t('Something went wrong'));
                }
            })
            .finally(() => {
                if (sequence === readSeq.current) {
                    setLoading(false);
                }
            });
    }, [dataUrl, filters]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const changeFilters = useCallback((change) => {
        setFilters((previous) => ({ ...previous, ...change }));
    }, []);

    const download = () => {
        const params = new URLSearchParams({ ...queryFor(filters), unit });

        window.location.assign(`${exportUrl}?${params.toString()}`);
    };

    if (error && !report) {
        return (
            <div className="tw-space-y-4">
                <Alert type="error">{error}</Alert>
                <Button variant="secondary" disabled={loading} onClick={fetchReport}>
                    {$t('Try again')}
                </Button>
            </div>
        );
    }

    if (loading && !report) {
        return <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">{$t('Loading')}…</p>;
    }

    if (!report) {
        return null;
    }

    const hoursInDay = report.options.hours_in_day;
    const weeks = report.weeks ?? EMPTY_LIST;

    return (
        <div className="tw-space-y-4">
            {!!error && <Alert type="error">{error}</Alert>}

            <div>
                {/* h2, not h1: inc/navbar.blade.php already yields the page
                    title as the page's h1. */}
                <h2 className="table__header tw-mb-1">{$t('My time summary')}</h2>
                <p className="tw-text-sm tw-font-medium tw-text-charcoal-50 tw-mb-0">
                    {$t('Everything you have recorded, by week, project and role. Submitted weeks are read-only; open a draft week to keep editing.')}
                </p>
            </div>

            {!report.has_any_time ? (
                <div className="table__card tw-space-y-3">
                    <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">{$t('You have not recorded any time yet.')}</p>
                    <Button as="a" href={recordUrl} variant="primary" className="tw-no-underline hover:tw-no-underline">
                        {$t('Go to My time record')}
                    </Button>
                </div>
            ) : (
                <>
                    <TimeSummaryFilters
                        filters={filters}
                        projects={report.project_options ?? EMPTY_LIST}
                        unit={unit}
                        onChange={changeFilters}
                        onUnitChange={setUnit}
                        onDownload={download}
                        downloadDisabled={!isComplete(filters)}
                    />

                    {/* A half-filled custom range would otherwise leave the
                        previous period's figures on screen under the new filter. */}
                    {!isComplete(filters) ? (
                        <div className="table__card">
                            <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">
                                {filters.from && filters.to ? $t('The end date must be on or after the start date.') : $t('Pick a start and end date')}
                            </p>
                        </div>
                    ) : (
                        <>
                            <TimeSummaryTiles tiles={report.tiles} unit={unit} hoursInDay={hoursInDay} />

                            <div className="table__card tw-space-y-4">
                                <div className="tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-2">
                                    <h3 className="tw-text-charcoal tw-text-base tw-font-semibold tw-mb-0">
                                        {unit === UNIT_HOURS ? $t('Hours by week, project and role') : $t('Days by week, project and role')}
                                    </h3>
                                    <span className="tw-text-sm tw-text-charcoal-50">{$t('Click a week to open it')}</span>
                                </div>

                                {weeks.length === 0 ? (
                                    <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">{$t('There are no weeks in this period up to the current week.')}</p>
                                ) : (
                                    <TimeSummaryTable
                                        weeks={weeks}
                                        projects={report.projects ?? EMPTY_LIST}
                                        grandTotal={report.grand_total}
                                        unit={unit}
                                        hoursInDay={hoursInDay}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}

if (document.getElementById('MyTimeSummary')) {
    const rootEl = document.getElementById('MyTimeSummary');

    ReactDOM.render(
        <App>
            <MyTimeSummaryIndex
                dataUrl={rootEl.getAttribute('data-data-url')}
                exportUrl={rootEl.getAttribute('data-export-url')}
                recordUrl={rootEl.getAttribute('data-record-url')}
            />
        </App>,
        rootEl,
    );
}
